import { toCanvasCoords } from '../../utils/canvas';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
}

interface SoundManagerLike {
    unlockAudio(): void;
}

interface TouchPosition {
    x: number;
    y: number;
}

interface TouchConfirmState {
    pressed: boolean;
    wasPressed: boolean;
}

interface TouchDelta {
    dx: number;
    dy: number;
    active: boolean;
}

/**
 * 터치 입력 처리
 */
export class TouchInput {
    private soundManager: SoundManagerLike | null;
    private renderer: RendererLike | null;
    private touchCurrent: TouchPosition;
    private touchPrevious: TouchPosition;
    private touchDelta: TouchPosition;
    private touchActive: boolean;
    private touchId: number | null;
    private touchConfirm: TouchConfirmState;
    public isTouchDevice: boolean;
    private canvas: HTMLCanvasElement | null = null;
    private touchstartHandler: ((e: TouchEvent) => void) | null = null;
    private touchmoveHandler: ((e: TouchEvent) => void) | null = null;
    private touchendHandler: ((e: TouchEvent) => void) | null = null;
    private touchcancelHandler: ((e: TouchEvent) => void) | null = null;

    constructor(soundManager: SoundManagerLike | null = null) {
        this.soundManager = soundManager;
        this.renderer = null;

        this.touchCurrent = { x: 0, y: 0 };
        this.touchPrevious = { x: 0, y: 0 };
        this.touchDelta = { x: 0, y: 0 };
        this.touchActive = false;
        this.touchId = null;
        this.touchConfirm = { pressed: false, wasPressed: false };

        this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        if (this.isTouchDevice) {
            this.setup();
        }
    }

    setRenderer(renderer: RendererLike): void {
        this.renderer = renderer;
    }

    setup(): void {
        this.canvas = document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (!this.canvas) return;

        this.touchstartHandler = (e: TouchEvent) => {
            e.preventDefault();
            for (const touch of Array.from(e.changedTouches)) {
                this.handleStart(touch);
            }
        };

        this.touchmoveHandler = (e: TouchEvent) => {
            e.preventDefault();
            for (const touch of Array.from(e.changedTouches)) {
                this.handleMove(touch);
            }
        };

        this.touchendHandler = (e: TouchEvent) => {
            e.preventDefault();
            for (const touch of Array.from(e.changedTouches)) {
                this.handleEnd(touch);
            }
        };

        this.touchcancelHandler = (e: TouchEvent) => {
            e.preventDefault();
            for (const touch of Array.from(e.changedTouches)) {
                this.handleEnd(touch);
            }
        };

        this.canvas.addEventListener('touchstart', this.touchstartHandler);
        this.canvas.addEventListener('touchmove', this.touchmoveHandler);
        this.canvas.addEventListener('touchend', this.touchendHandler);
        this.canvas.addEventListener('touchcancel', this.touchcancelHandler);
    }

    destroy(): void {
        if (this.canvas) {
            if (this.touchstartHandler) {
                this.canvas.removeEventListener('touchstart', this.touchstartHandler);
                this.touchstartHandler = null;
            }
            if (this.touchmoveHandler) {
                this.canvas.removeEventListener('touchmove', this.touchmoveHandler);
                this.touchmoveHandler = null;
            }
            if (this.touchendHandler) {
                this.canvas.removeEventListener('touchend', this.touchendHandler);
                this.touchendHandler = null;
            }
            if (this.touchcancelHandler) {
                this.canvas.removeEventListener('touchcancel', this.touchcancelHandler);
                this.touchcancelHandler = null;
            }
            this.canvas = null;
        }
        this.touchActive = false;
        this.touchId = null;
    }

    handleStart(touch: Touch): void {
        if (this.soundManager) {
            this.soundManager.unlockAudio();
        }

        const target = touch.target as HTMLElement;
        const rect = target.getBoundingClientRect();
        const pos = toCanvasCoords(touch.clientX, touch.clientY, rect, this.renderer ?? undefined);

        this.touchId = touch.identifier;
        this.touchActive = true;
        this.touchCurrent.x = pos.x;
        this.touchCurrent.y = pos.y;
        this.touchPrevious.x = pos.x;
        this.touchPrevious.y = pos.y;
        this.touchDelta.x = 0;
        this.touchDelta.y = 0;
        this.touchConfirm.pressed = true;
    }

    handleMove(touch: Touch): void {
        if (this.touchId !== touch.identifier) return;

        const target = touch.target as HTMLElement;
        const rect = target.getBoundingClientRect();
        const pos = toCanvasCoords(touch.clientX, touch.clientY, rect, this.renderer ?? undefined);

        this.touchDelta.x += pos.x - this.touchCurrent.x;
        this.touchDelta.y += pos.y - this.touchCurrent.y;
        this.touchCurrent.x = pos.x;
        this.touchCurrent.y = pos.y;
    }

    handleEnd(touch: Touch): void {
        if (this.touchId !== touch.identifier) return;

        this.touchActive = false;
        this.touchCurrent.x = 0;
        this.touchCurrent.y = 0;
        this.touchPrevious.x = 0;
        this.touchPrevious.y = 0;
        this.touchDelta.x = 0;
        this.touchDelta.y = 0;
        this.touchId = null;
        this.touchConfirm.pressed = false;
    }

    update(): void {
        this.touchConfirm.wasPressed = this.touchConfirm.pressed;
    }

    resetDelta(): void {
        this.touchDelta.x = 0;
        this.touchDelta.y = 0;
    }

    getDelta(): TouchDelta {
        if (this.touchActive) {
            return {
                dx: this.touchDelta.x,
                dy: this.touchDelta.y,
                active: true
            };
        }
        return { dx: 0, dy: 0, active: false };
    }

    isActive(): boolean {
        return this.touchActive;
    }

    isConfirmJustPressed(): boolean {
        return this.touchConfirm.pressed && !this.touchConfirm.wasPressed;
    }

    cancelConfirm(): void {
        this.touchConfirm.pressed = false;
        this.touchConfirm.wasPressed = false;
    }
}
